import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useMutation } from '@apollo/client';
import { formatString } from '../../utils';
import ActionButton from '../Common/ActionButton';
import ThreeDots from '../../assets/icons/more-vertical.svg';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { BeatLoader } from 'react-spinners';
import { UPDATE_SADDLE } from '../../apollo/mutations/updateSaddle';
import { createToast } from '../../utils/Toast';

const Toast = createToast();

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function SaddlesTable({ saddles, loading, sortConfig, handleSort, onEdit, onDelete, canEditQuantity = true }) {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [editingQtyKey, setEditingQtyKey] = useState(null);
  const [qtyDraft, setQtyDraft] = useState('');
  const numericColumns = ['price', 'quantity', 'status'];

  const [updateSaddleMutation, { loading: updatingQty }] = useMutation(UPDATE_SADDLE, {
    refetchQueries: ['listSaddlesForAdmin'],
  });

  const rowKey = (saddle) => `${saddle.pk}__${saddle.sk}`;

  const beginQtyEdit = (saddle) => {
    setEditingQtyKey(rowKey(saddle));
    setQtyDraft(String(saddle.quantity ?? 0));
  };

  const cancelQtyEdit = () => {
    setEditingQtyKey(null);
    setQtyDraft('');
  };

  const commitQtyEdit = async (saddle) => {
    const parsed = parseInt(qtyDraft, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      Toast.fire({ icon: 'error', title: 'Invalid quantity' });
      return;
    }
    if (parsed === (saddle.quantity ?? 0)) {
      cancelQtyEdit();
      return;
    }
    try {
      await updateSaddleMutation({
        variables: {
          input: {
            pk: saddle.pk,
            sk: saddle.sk,
            brandName: saddle.brandName,
            quantity: parsed,
          },
        },
      });
      Toast.fire({ icon: 'success', title: 'Quantity updated' });
      cancelQtyEdit();
    } catch (err) {
      Toast.fire({ icon: 'error', title: 'Failed to update quantity' });
      console.error('Error updating quantity:', err);
    }
  };
  
  const handleEdit = (saddle, isViewMode) => {
    onEdit(saddle, isViewMode);
    setDropdownOpen(null);
  };

  const handleDelete = (saddle) => {
    onDelete(saddle);
    setDropdownOpen(null);
  };


  const sortedSaddles = React.useMemo(() => {
    if (sortConfig.key && saddles?.length) {
      return [...saddles].sort((a, b) => {
        let aValue = a[sortConfig.key] ?? "";
        let bValue = b[sortConfig.key] ?? "";

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;

        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      });
    }
    return saddles;
  }, [saddles, sortConfig]);

  const hideQuantityColumn = saddles?.[0]?.type === "PRODUCT";

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <BeatLoader color={'#2b364b'} />
      </div>
    );
  }

  if (!saddles || saddles?.length === 0) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-3xl text-gray-500">
          No Saddles Found. Please check back later.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full overflow-hidden"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
        }}
      >
        <thead className="thead">
          <tr className="table-head">
            {[
              { key: 'brandName', label: 'Brand Name' },
              { key: 'productTitle', label: 'Product Title' },
              { key: 'price', label: 'Price' },
              !hideQuantityColumn && { key: 'quantity', label: 'Quantity' },
              { key: 'condition', label: 'Condition' },
              { key: 'discipline', label: 'Discipline' },
              { key: 'status', label: 'Status' }
            ].map(({ key, label }) =>
              key && (
                <th
                  key={key}
                  scope="col"
                  align={numericColumns.includes(key) ? 'center' : 'left'}
                  className="px-3 py-2 group"
                  style={{
                    borderBottom: '1px solid #999',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort(key)}
                >
                  {label}
                  <span className="text-sm ml-1">
                    {sortConfig.key === key ? (
                      sortConfig.direction === 'ascending' ? '↑' : '↓'
                    ) : (
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        ↑
                      </span>
                    )}
                  </span>
                </th>
              )
            )}
            <th
              scope="col"
              className="px-3 py-2"
              style={{
                borderBottom: '1px solid #999',
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedSaddles?.map((saddle, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={(e) =>  handleEdit(saddle, true)}
            >
              <td align="left" className="table-order px-3 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #999' }}>
                {formatString(saddle.brandName) || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {formatString(saddle.productTitle) || 'N/A'}
              </td>
              <td align="center" className="px-3 py-2 whitespace-nowrap table-order" style={{ borderBottom: '1px solid #999' }}>
                {saddle.price ? `$${Number(saddle.price).toLocaleString()}` : 'N/A'}
              </td>
              {!hideQuantityColumn && (
                <td
                  align="center"
                  className="px-3 py-2 whitespace-nowrap table-order"
                  style={{ borderBottom: '1px solid #999' }}
                  onClick={canEditQuantity ? (e) => {
                    e.stopPropagation();
                    if (editingQtyKey !== rowKey(saddle)) beginQtyEdit(saddle);
                  } : undefined}
                >
                  {editingQtyKey === rowKey(saddle) ? (
                    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="number"
                        min="0"
                        autoFocus
                        disabled={updatingQty}
                        value={qtyDraft}
                        onChange={(e) => setQtyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitQtyEdit(saddle);
                          if (e.key === 'Escape') cancelQtyEdit();
                        }}
                        onBlur={() => commitQtyEdit(saddle)}
                        className="w-16 px-1 py-0.5 border border-gray-400 rounded text-center"
                      />
                      {updatingQty && <BeatLoader size={6} color={'#2b364b'} />}
                    </div>
                  ) : (
                    <span className={canEditQuantity ? 'cursor-pointer hover:underline' : ''}>
                      {saddle.quantity ?? 'N/A'}
                    </span>
                  )}
                </td>
              )}
              <td align="left" className="table-buyer px-3 py-2 whitespace-nowrap text-[16px]" style={{ borderBottom: '1px solid #999' }}>
                {formatString(saddle.newOrUsed) || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {formatString(saddle.discipline) || 'N/A'}
              </td>
              <td align="center" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {formatString(saddle.status) || 'N/A'}
              </td>
              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap table-order"
                style={{ borderBottom: '1px solid #999' }}
                onClick={e => e.stopPropagation()}
              >
                <ActionButton
                  icon={ThreeDots}
                  index={index}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  actions={[
                    {
                      label: 'Edit Saddle',
                      icon: FaEdit,
                      onClick: () => handleEdit(saddle),
                      className: 'text-gray-700',
                    },
                    {
                      label: 'Delete Saddle',
                      icon: FaTrashAlt,
                      onClick: () => handleDelete(saddle),
                      className: 'text-red-600',
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SaddlesTable;
